import {isUndefined} from 'lodash';
import React, {FC, PropsWithChildren, ReactNode, RefObject} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, Renderer, RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {UserTheme, useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {CrmSyncSourcesEnum, CrmSyncStatusesEnum} from '../../../models/api/crmSyncModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {Dialog} from '../../common/dialogs/dialog';
import {DialogContent} from '../../common/dialogs/dialogContent';
import {DialogFooter} from '../../common/dialogs/dialogFooter';
import {DialogHeader} from '../../common/dialogs/dialogHeader';
import {CrmSyncUpsellContainer} from './common/crmSyncUpsellContainer';

/*
 * Constants.
 */

const dialogWidth = 600; // px.
const dialogMaxHeight = 650; // px.

/*
 * Props.
 */

interface CrmSyncDialogProps {
  title: ReactNode;
  source: CrmSyncSourcesEnum;
  isInfoBoxExpanded: boolean;
  status?: CrmSyncStatusesEnum;
  numAccounts?: number;
  isSyncDisabled?: boolean;
  contentWrapperRef?: RefObject<HTMLDivElement>;
  isAdmin: boolean;
  syncButtonText?: ReactNode;
  onRequestClose: Handler;
  onToggleInfoBox: Handler;
  onSyncClick: Handler;
  onDisableSyncClick: Handler;
  hasCrmSyncFeature: boolean;
  onBackClick?: Handler;
  renderInfoBox?: RendererOf<Handler>;
  renderBanners?: Renderer;
}

/*
 * Style.
 */

const StyledInfoBoxWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
  padding: 20px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};

  &:empty {
    padding: 0;
    border: none;
  }
`;

const StyledUpsellWrapperDiv = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledCancelButton = styled(Button)`
  margin-left: auto;
`;

const StyledSyncButton = styled(Button)`
  margin-left: 10px;
`;

const StyledDialogContent = styled(DialogContent)`
  display: grid;
  margin: 0;
  flex: 1;
`;

const StyledDialogContentWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  overflow: auto;
  flex: 1;
`;

/*
 * Component.
 */

export const CrmSyncDialog: FC<PropsWithChildren<CrmSyncDialogProps>> = (props) => {
  const {
    title,
    children,
    isSyncDisabled,
    contentWrapperRef,
    numAccounts,
    status,
    isAdmin,
    syncButtonText,
    source,
    onRequestClose,
    onSyncClick,
    onDisableSyncClick,
    onBackClick,
    hasCrmSyncFeature,
  } = props;
  const theme = useUserTheme();

  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CRM_SYNC_VIEW, {source});
  });

  return (
    <Dialog width={dialogWidth} height={dialogMaxHeight}>
      <DialogHeader
        title={title}
        customButtons={maybeRenderCustomButtons(props, theme)}
        onBackClick={onBackClick}
      />
      <StyledDialogContentWrapperDiv ref={contentWrapperRef}>
        {hasCrmSyncFeature ? maybeRenderBanners(props) : renderUpsell(props.source)}
        <StyledDialogContent>{children}</StyledDialogContent>
      </StyledDialogContentWrapperDiv>
      <DialogFooter>
        {maybeRenderDisableButton(onDisableSyncClick, isAdmin, status)}
        <ButtonGroupSpacer />
        <StyledCancelButton onClick={onRequestClose}>
          <Trans id="accounts-crm-dialog-cancel">Cancel</Trans>
        </StyledCancelButton>
        <StyledSyncButton
          onClick={onSyncClick}
          variant={ButtonVariantsEnum.PRIMARY}
          isDisabled={isSyncDisabled}
        >
          {syncButtonText ?? renderSyncButtonText(status, numAccounts)}
        </StyledSyncButton>
      </DialogFooter>
    </Dialog>
  );
};

/*
 * Helpers.
 */

function maybeRenderDisableButton(
  onDisableSyncClick: Handler,
  isAdmin: boolean,
  status?: CrmSyncStatusesEnum,
) {
  if (!status || status === CrmSyncStatusesEnum.DISABLED) {
    return null;
  }
  return (
    <Button variant={ButtonVariantsEnum.SECONDARY_DANGER} onClick={onDisableSyncClick} isDisabled={!isAdmin}>
      <Trans id="accounts-crm-dialog-disable-sync">Disable sync</Trans>
    </Button>
  );
}

function renderSyncButtonText(status?: CrmSyncStatusesEnum, numAccounts?: number) {
  if (isUndefined(numAccounts)) {
    if (status === CrmSyncStatusesEnum.DISABLED) {
      return <Trans id="accounts-crm-dialog-sync-accounts">Sync accounts</Trans>;
    }
    return <Trans id="accounts-crm-dialog-sync-accounts-now">Sync accounts now</Trans>;
  }

  if (status === CrmSyncStatusesEnum.DISABLED) {
    return (
      <Plural
        id="accounts-crm-dialog-sync-num-accounts"
        value={numAccounts}
        one={`Sync ${numAccounts.toLocaleString()} account`}
        other={`Sync ${numAccounts.toLocaleString()} accounts`}
      />
    );
  }

  return (
    <Plural
      id="accounts-crm-dialog-sync-num-accounts-now"
      value={numAccounts}
      one={`Sync ${numAccounts.toLocaleString()} account now`}
      other={`Sync ${numAccounts.toLocaleString()} accounts now`}
    />
  );
}

function maybeRenderCustomButtons(props: CrmSyncDialogProps, theme: UserTheme) {
  const {isInfoBoxExpanded, renderInfoBox, onToggleInfoBox} = props;
  const iconColor = isInfoBoxExpanded ? theme.palette.blue.shade40 : undefined;
  if (!renderInfoBox) {
    return null;
  }
  return (
    <IconButton
      iconName={isInfoBoxExpanded ? 'infoFilled' : 'info'}
      label={null}
      onClick={onToggleInfoBox}
      color={iconColor}
      hoverColor={iconColor}
    />
  );
}

function maybeRenderBanners(props: CrmSyncDialogProps) {
  const {isInfoBoxExpanded, renderInfoBox, onToggleInfoBox, renderBanners} = props;
  if (!renderInfoBox && !renderBanners) {
    return null;
  }
  return (
    <StyledInfoBoxWrapperDiv>
      {isInfoBoxExpanded && renderInfoBox && renderInfoBox(onToggleInfoBox)}
      {renderBanners && renderBanners()}
    </StyledInfoBoxWrapperDiv>
  );
}

/**
 * Only render upsell for non-growth
 */
function renderUpsell(source: CrmSyncSourcesEnum) {
  return (
    <StyledUpsellWrapperDiv>
      <CrmSyncUpsellContainer crmSyncSource={source} />
    </StyledUpsellWrapperDiv>
  );
}
